import { PayloadAction, createSlice } from "@reduxjs/toolkit"
import { StepStatus } from "../practiceProfile/practiceProfileProcessSlice"
import { RootState } from "../../reduxStore/store"
import { providerUpdated } from "../../domain/Provider/model"
import { Provider } from "../../domain/Provider/schema"
import { isProviderComplete } from "../../domain/ViewModel/helpers/providerHelpers"
import { updateUser } from "../../domain/Account/userSlice"

export interface Step {
  name: string
  status: StepStatus
}

interface OnboardingProcessState {
  steps: Step[]
  completed: boolean
}

const initialState: OnboardingProcessState = {
  steps: [
    { name: "registration", status: "notStarted" },
    { name: "verifyEmail", status: "notStarted" },
    { name: "mfaEnrollment", status: "notStarted" },
    { name: "termsOfUse", status: "notStarted" },
    { name: "practiceSetup", status: "notStarted" },
    { name: "profileSetup", status: "notStarted" }, //this is provider - need to change naming convention throughout app
    { name: "activation", status: "notStarted" },
  ],
  completed: false,
}

// TODO: remove this whole slice - this was part of the old onboarding process and has been deprecated
const onboardingProcessSlice = createSlice({
  name: "onboardingProcess",
  initialState,
  reducers: {
    touStarted: (state) => {
      const index = getStepIndex(state.steps, "termsOfUse")
      state.steps[index].status = "pending"
    },
    practiceSetupStarted: (state) => {
      const index = getStepIndex(state.steps, "practiceSetup")
      state.steps[index].status = "pending"
    },
    statusUpdated: (state, action: PayloadAction<boolean>) => {
      state.completed = action.payload
    },
    allStepsCompleteUpdated: (state, action: PayloadAction<boolean>) => {
      state.completed = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateUser, (state, action) => {
        const index = getStepIndex(state.steps, "termsOfUse")
        const nextIndex = index + 1
        if (!!action.payload.touAcceptDateTime) {
          state.steps[index].status = "complete"
          if (state.steps[nextIndex].status === "notStarted") {
            state.steps[nextIndex].status = "pending"
          }
        }
      })
      .addCase("termsOfUse/started", (state) => {
        const index = getStepIndex(state.steps, "termsOfUse")
        state.steps[index].status = "pending"
      })

      .addCase("practiceSetup/started", (state) => {
        const index = getStepIndex(state.steps, "practiceSetup")
        state.steps[index].status = "pending"
      })

      .addCase("authentication/authenticated", (state) => {
        const registrationIndex = getStepIndex(state.steps, "registration")
        const mfaIndex = getStepIndex(state.steps, "mfaEnrollment")
        const verifyEmailIndex = getStepIndex(state.steps, "verifyEmail")
        const tosIndex = getStepIndex(state.steps, "termsOfUse")

        state.steps[registrationIndex].status = "complete"
        state.steps[mfaIndex].status = "complete"
        state.steps[verifyEmailIndex].status = "complete"
        state.steps[tosIndex].status = "ready"
      })

      // TODO: update this when we start whatever feature comes after provider
      .addCase(providerUpdated, (state, action: PayloadAction<Provider>) => {
        const providerIndex = getStepIndex(state.steps, "profileSetup")
        const providerIsComplete = isProviderComplete(action.payload)

        if (providerIsComplete) {
          state.steps[providerIndex].status = "complete"
          // add new case here
        } else {
          state.steps[providerIndex].status = "pending"
        }
      })

    // TODO: add something similar but for Provider here:
    // .addCase(providerUpdated, (state, action: PayloadAction<Provider>) => {
    //   const providerIndex = getStepIndex(state.steps, "profileSetup")
    //   const activationIndex = getStepIndex(state.steps, "activation")
    //   const providerIsComplete = isProviderComplete(action.payload)

    //   if (providerIsComplete) {
    //     state.steps[providerIndex].status = "complete"
    //     state.steps[activationIndex].status = "ready"
    //   } else {
    //     state.steps[providerIndex].status = "ready"
    //     state.steps[activationIndex].status = "pending"
    //   }
    // })
  },
})

export const { touStarted, practiceSetupStarted, statusUpdated } =
  onboardingProcessSlice.actions

export const selectIsOnboarded = (state: RootState): boolean => {
  return state.onboardingProcess.completed == true
}

export const selectStepStatuses = (state: RootState): Step[] => {
  return state.onboardingProcess.steps
}

const getStepIndex = (steps: Step[], name: string) => {
  return steps.findIndex((step) => step.name === name)
}

export default onboardingProcessSlice.reducer
